import Analytics from '@opencare/analytics';
import { IconInfoCircle } from '@tabler/icons-react';

import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { PrimaryButton } from 'brand-guidelines/Buttons-styled-components';
import { FieldValidation, useFormValidator } from 'common/Validator/Validator';
import { UpdatePatientDetails } from 'connect/saga/handlers/patient/update-details';
import { selectDataPatient } from 'connect/selectors';
import { InputType } from 'pages/checkout/components/common/Input';
import { FormError } from 'pages/checkout/components/common/input-styled-components';
import { StepFormWrapper, StepHeader, StepHeaderSubtext, StepHeaderText } from 'pages/checkout/components/common/layout-styled-components';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState, DateOfBirth, SexAtBirth } from 'pages/checkout/connect/types';
import { DAY_OPTIONS, MONTH_OPTIONS, YEAR_OPTIONS } from 'pages/checkout/constants';
import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { formatBirthDate, updateDayOptions } from 'pages/checkout/utils/timeUtils';

import { useAnalyticsService } from 'services/AnalyticsService';
import { BooleanChoice, booleanChoiceToBoolean } from 'types/BooleanChoice';
import { ExperimentName, EXPERIMENTS } from 'utils/constants/experiment-configs';
import { useEffectOnce } from 'utils/customHooks/useEffectOnce';
import { useExperiment } from 'utils/customHooks/useExperiment';
import { usePatch } from 'utils/customHooks/usePatch';

import { googlePlaceActionLink } from 'utils/google';

import { SecurityBadgesLogos } from '../Account/components/SecurityBadges/SecurityBadgesLogos';
import { SecurityBadgesText } from '../Account/components/SecurityBadges/SecurityBadgesText';

import { Alert } from './Alert';
import { ALERT_THEMES } from './Alert-styled-components';
import { PATIENT_INFO_VALIDATOR_CONFIG } from './form-validator-configs';
import { FormRow, InsuranceStepWrapper } from './Insurance-styled-components';
import { commonBirthDateConfig, FormInputConfig, FormInputs } from './renderer-helpers';

const analytics = new Analytics();
const sexOptions = [SexAtBirth.Female, SexAtBirth.Male];

// Process sex assigned at birth into API model attribute
const sexAssignedAtBirthToGender = (sexAssignedAtBirth: SexAtBirth) => {
  const sexToGenderMap = {
    [SexAtBirth.Female]: 'F',
    [SexAtBirth.Male]: 'M',
  };

  return sexToGenderMap[sexAssignedAtBirth] ?? 'O';
};

const genderToSexAssignedAtBirth = (gender: string): SexAtBirth | null => {
  const genderToSexAssignedAtBirthMap = {
    F: SexAtBirth.Female,
    M: SexAtBirth.Male,
  } as Record<string, SexAtBirth>;

  return genderToSexAssignedAtBirthMap[gender] ?? null;
};

interface ValidationState {
  allValid?: boolean;
  firstName?: FieldValidation;
  lastName?: FieldValidation;
  month?: FieldValidation;
  day?: FieldValidation;
  year?: FieldValidation;
  sexAssignedAtBirth?: FieldValidation;
  isReturningPatient?: FieldValidation;
}

export const PatientInfo = () => {
  const patchCheckout = usePatch<CheckoutState>({
    stateHandler: CheckoutStateHandler,
  });
  const analyticsService = useAnalyticsService();
  const patientFormValidator = useFormValidator(PATIENT_INFO_VALIDATOR_CONFIG);
  const { isVariant: isSecurityMessagingVariant } = useExperiment({
    experiment: EXPERIMENTS[ExperimentName.CHECKOUT_SECURITY_MESSAGING],
  });

  const [dayOptions, setDayOptions] = useState<{ id: string; name: string; }[]>(DAY_OPTIONS);
  const [submitting, setSubmitting] = useState(false);

  const { patient: patientDataRedux } = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const isLoading = useSelector(UpdatePatientDetails.loading!.selector);
  const error = useSelector(UpdatePatientDetails.error!.selector);
  const success = useSelector(UpdatePatientDetails.success!.selector);
  const { goToNextStep } = useNextCheckoutStep();

  const patientDataDb = useSelector(selectDataPatient);

  const storedFirstName = useMemo<string>(
    () => patientDataDb?.firstName || patientDataRedux?.firstName || '',
    [patientDataDb, patientDataRedux],
  );
  const storedLastName = useMemo<string>(
    () => patientDataDb?.lastName || patientDataRedux?.lastName || '',
    [patientDataDb, patientDataRedux],
  );
  const storedSexAssignedAtBirth = useMemo<SexAtBirth | null>(
    () => genderToSexAssignedAtBirth(patientDataDb?.gender) || patientDataRedux?.sexAssignedAtBirth || null,
    [patientDataDb, patientDataRedux],
  );

  const storedDateOfBirth = useMemo<DateOfBirth | null>(() => {
    if (patientDataDb?.dateOfBirth) {
      const dateOfBirthMoment = moment(patientDataDb.dateOfBirth);
      const year = dateOfBirthMoment.year().toString();
      // moment is 0-indexed for months, so we need to add 1
      const month = (dateOfBirthMoment.month() + 1).toString();
      const day = dateOfBirthMoment.date().toString();

      return { year, month: month.padStart(2, '0'), day: day.padStart(2, '0') };
    }

    if (patientDataRedux?.dateOfBirth) {
      return patientDataRedux.dateOfBirth;
    }

    return null;
  }, [patientDataDb, patientDataRedux]);

  const [firstName, setFirstName] = useState(storedFirstName);
  const [lastName, setLastName] = useState(storedLastName);
  const [year, setYear] = useState(storedDateOfBirth?.year || null);
  const [month, setMonth] = useState(storedDateOfBirth?.month || null);
  const [day, setDay] = useState(storedDateOfBirth?.day || null);
  const [sexAssignedAtBirth, setSexAssignedAtBirth] = useState(storedSexAssignedAtBirth);
  const [isReturningPatient, setIsReturningPatient] = useState<BooleanChoice | null>(null);

  const updateValidDays = ({ selectedYear, selectedMonth }: { selectedYear?: string; selectedMonth?: string; }) => {
    const filteredDayOptions = updateDayOptions({
      selectedYear,
      selectedMonth,
      savedYear: year,
      savedMonth: month,
    });

    // Update day options if needed
    setDayOptions((prevDayOptions) => {
      if (prevDayOptions.length === filteredDayOptions.length) {
        return prevDayOptions;
      }
      return filteredDayOptions;
    });

    if (!filteredDayOptions.some((option) => option.id === day)) {
      setDay(null);
    }
  };

  const showReturningPatientToggle = Boolean(
    googlePlaceActionLink.getConversionData().rwgToken,
  );

  const formState = useMemo(() => ({
    firstName,
    lastName,
    month,
    day,
    year,
    sexAssignedAtBirth,
    ...showReturningPatientToggle && {
      isReturningPatient,
    },
  }), [
    firstName,
    lastName,
    month,
    day,
    year,
    sexAssignedAtBirth,
    isReturningPatient,
    showReturningPatientToggle,
  ]);

  const [formValidity, setFormValidity] = useState<ValidationState>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const handleValidate = useCallback(({
    stateOverride,
  }: {
    stateOverride?: Partial<Record<keyof ValidationState, any>>;
  } = {}) => {
    const validationStateToUse = {
      ...formState,
      ...stateOverride,
    };

    const validationResult = patientFormValidator.validate(validationStateToUse, { showReturningPatientToggle });
    setFormValidity(validationResult);
    return validationResult;
  }, [formState, showReturningPatientToggle, patientFormValidator]);

  useEffect(() => {
    if (!formState) {
      return;
    }

    handleValidate();
  }, [handleValidate, formState]);

  const handleYearSelect = (selectedYear: string) => {
    setYear(selectedYear);

    if (selectedYear) {
      updateValidDays({ selectedYear });
    }
  };

  const handleMonthSelect = (selectedMonth: string) => {
    setMonth(selectedMonth);

    if (selectedMonth) {
      updateValidDays({ selectedMonth });
    }
  };

  const handleDaySelect = (selectedDay: string) => {
    setDay(selectedDay);
  };

  const handleSexAtBirthSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSexAssignedAtBirth = event.target.value as SexAtBirth;
    setSexAssignedAtBirth(newSexAssignedAtBirth);
  };

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFirstName = event.target.value;
    setFirstName(newFirstName);
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLastName = event.target.value;
    setLastName(newLastName);
  };

  const handleReturningPatientToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newReturningPatient = event.target.value as BooleanChoice;
    setIsReturningPatient(newReturningPatient);
  };

  const dispatch = useDispatch();

  // Track page view on mount
  useEffectOnce(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-patient-info',
      name: 'checkout',
      section: 'patient-info',
      ...analyticsService.analyticsData,
    });
  });

  const handleNextStep = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isFormValid = handleValidate();
    if (!isFormValid.allValid) {
      setShowValidationErrors(true);
      return;
    }

    // This isn't necessary sine we checked this in the form validator, but TS doesn't know that
    if (!year || !month || !day || !sexAssignedAtBirth) {
      setShowValidationErrors(true);
      return;
    }

    const patientDetails = {
      firstName,
      lastName,
      sexAssignedAtBirth: sexAssignedAtBirth!,
      dateOfBirth: { year, month, day },
      ...showReturningPatientToggle && {
        isReturningPatient: booleanChoiceToBoolean(isReturningPatient!),
      },
    };

    const birthDateString = formatBirthDate({ year, month, day });

    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-patient-info',
      name: 'checkout',
      section: 'patient-info',
      isReturningPatient: showReturningPatientToggle
        ? Boolean(isReturningPatient)
        : undefined,
    });

    patchCheckout({
      patient: patientDetails,
    });

    const patientDetailsForApi = {
      firstName,
      lastName,
      gender: sexAssignedAtBirthToGender(sexAssignedAtBirth!),
      dateOfBirth: birthDateString,
    };

    setSubmitting(true);
    // Update patient in API
    dispatch(UpdatePatientDetails.createAction({ patientDetails: patientDetailsForApi, autoVerify: true }));
  };

  // If form is invalid, show validation errors when clicking the button
  const handleDisabledButtonClick = () => {
    if (formValidity.allValid) {
      return;
    }

    setShowValidationErrors(true);
  };

  // Watch for success state changes
  useEffect(() => {
    if (!submitting || !success || isLoading) {
      return;
    }

    // Mark success as handled
    dispatch(UpdatePatientDetails.success!.update(null));
    goToNextStep();
  }, [success, isLoading, dispatch, goToNextStep, submitting]);

  const nameInputs: FormInputConfig[] = [
    {
      label: 'First Name',
      type: InputType.TEXT,
      autoComplete: 'given-name',
      required: true,
      value: firstName,
      onChange: handleFirstNameChange,
      errorMessage: showValidationErrors ? formValidity.firstName?.message : undefined,
    },
    {
      label: 'Last Name',
      type: InputType.TEXT,
      autoComplete: 'family-name',
      required: true,
      value: lastName,
      onChange: handleLastNameChange,
      errorMessage: showValidationErrors ? formValidity.lastName?.message : undefined,
    },
  ];

  const dateOfBirthInputs: FormInputConfig[] = [
    {
      ...commonBirthDateConfig,
      label: 'Birth Month',
      autoComplete: 'bday-month',
      required: true,
      options: MONTH_OPTIONS,
      value: month,
      onSelect: handleMonthSelect,
      errorMessage: showValidationErrors ? formValidity.month?.message : undefined,
    },
    {
      ...commonBirthDateConfig,
      label: 'Birth Day',
      autoComplete: 'bday-day',
      required: true,
      options: dayOptions,
      value: day,
      onSelect: handleDaySelect,
      errorMessage: showValidationErrors ? formValidity.day?.message : undefined,
    },
    {
      ...commonBirthDateConfig,
      label: 'Birth Year',
      autoComplete: 'bday-year',
      required: true,
      options: YEAR_OPTIONS,
      value: year,
      onSelect: handleYearSelect,
      errorMessage: showValidationErrors ? formValidity.year?.message : undefined,
    },
  ];

  const sexAtBirthInput: FormInputConfig[] = [
    {
      label: 'Sex assigned at birth',
      tooltip: 'This information is needed for insurance purposes. If you\'d like, you can share your gender details in the notes section on the next page.',
      type: InputType.TOGGLE,
      required: true,
      options: sexOptions,
      value: sexAssignedAtBirth,
      onChange: handleSexAtBirthSelect,
      errorMessage: showValidationErrors ? formValidity.sexAssignedAtBirth?.message : undefined,
    },
  ];

  const returningPatientToggle: FormInputConfig[] = [
    {
      label: 'Have you visited this practice before?',
      type: InputType.TOGGLE,
      required: showReturningPatientToggle,
      value: isReturningPatient,
      onChange: handleReturningPatientToggleChange,
      options: [BooleanChoice.Yes, BooleanChoice.No],
      errorMessage: showValidationErrors ? formValidity.isReturningPatient?.message : undefined,
    },
  ];

  return (
    <InsuranceStepWrapper onSubmit={handleNextStep}>
      <StepHeader>
        <StepHeaderText>Patient Information</StepHeaderText>
        <StepHeaderSubtext>Who is coming in to see the dentist?</StepHeaderSubtext>
        <SecurityBadgesText />
      </StepHeader>

      <StepFormWrapper>
        {!isSecurityMessagingVariant && (
          <Alert theme={ALERT_THEMES.success} customIcon={<IconInfoCircle />}>
            Enter your details as shown on your government issued ID
          </Alert>
        )}

        {error && <FormError>{error}</FormError>}

        <FormRow>
          <FormInputs inputConfigs={nameInputs} />
        </FormRow>

        <FormRow>
          <FormInputs inputConfigs={dateOfBirthInputs} />
        </FormRow>

        <FormRow>
          <FormInputs inputConfigs={sexAtBirthInput} />
        </FormRow>

        {showReturningPatientToggle
          && (
            <FormRow>
              <FormInputs inputConfigs={returningPatientToggle} />
            </FormRow>
          )}

      </StepFormWrapper>
      <PrimaryButton
        type="submit"
        onClick={handleDisabledButtonClick}
        $loading={isLoading}
        $disabled={!formValidity.allValid}
      >
        Next Step
      </PrimaryButton>
      <SecurityBadgesLogos />
    </InsuranceStepWrapper>
  );
};
